import { getPresenceUserColor } from '@baserow/modules/core/utils/presenceColors'

export const state = () => ({
  spaces: {},
})

export const mutations = {
  SET_MEMBERS(state, { space, entries }) {
    const members = {}
    for (const entry of entries) {
      members[entry.presence_id] = {
        user_id: entry.user_id,
        focus: entry.focus || null,
      }
    }
    state.spaces = { ...state.spaces, [space]: { members } }
  },
  ADD_MEMBER(state, { space, presence_id, user_id }) {
    const spaceData = state.spaces[space]
    if (!spaceData) return
    spaceData.members = {
      ...spaceData.members,
      [presence_id]: { user_id, focus: null },
    }
    state.spaces = { ...state.spaces }
  },
  REMOVE_MEMBER(state, { space, presence_id }) {
    const spaceData = state.spaces[space]
    if (!spaceData) return
    const { [presence_id]: _, ...rest } = spaceData.members
    state.spaces = {
      ...state.spaces,
      [space]: { members: rest },
    }
  },
  SET_FOCUS(state, { space, presence_id, focus }) {
    // Focus frames are the presence hot path; mutating in place keeps the
    // space and member identities stable so only consumers of this member's
    // focus re-render (state is deeply reactive in Vuex 4).
    const member = state.spaces[space]?.members[presence_id]
    if (!member) return
    member.focus = focus
  },
  CLEAR_SPACE(state, { space }) {
    const { [space]: _, ...rest } = state.spaces
    state.spaces = rest
  },
  CLEAR_ALL_SPACES(state) {
    state.spaces = {}
  },
}

export const actions = {
  handleMembers({ commit }, { space, entries }) {
    commit('SET_MEMBERS', { space, entries })
  },
  handleJoin({ commit }, { space, presence_id, user_id }) {
    commit('ADD_MEMBER', { space, presence_id, user_id })
  },
  handleLeave({ commit }, { space, presence_id }) {
    commit('REMOVE_MEMBER', { space, presence_id })
  },
  handleFocus({ commit }, { space, presence_id, focus }) {
    commit('SET_FOCUS', { space, presence_id, focus })
  },
  clearSpace({ commit }, { space }) {
    commit('CLEAR_SPACE', { space })
  },
  clearAllSpaces({ commit }) {
    commit('CLEAR_ALL_SPACES')
  },
}

// Shared for every no-focus result so consumer prop identity stays stable in
// the common case where nobody focuses anything.
const EMPTY_FOCUS_MAP = new Map()

function _compareFocusEntries(a, b) {
  if (a.user_id !== b.user_id) return a.user_id - b.user_id
  if (a.presence_id < b.presence_id) return -1
  if (a.presence_id > b.presence_id) return 1
  return 0
}

function _buildFocusMap(members, focusType, keyFn) {
  let map = null
  for (const [presence_id, data] of Object.entries(members)) {
    if (!data.focus || data.focus.type !== focusType) continue
    const key = keyFn(data.focus)
    if (map === null) map = new Map()
    if (!map.has(key)) map.set(key, [])
    map.get(key).push({
      presence_id,
      user_id: data.user_id,
      editing: data.focus.editing || false,
      color: getPresenceUserColor(data.user_id),
    })
  }
  if (map === null) return EMPTY_FOCUS_MAP
  for (const entries of map.values()) {
    entries.sort(_compareFocusEntries)
  }
  return map
}

export const getters = {
  getUniqueUsersBySpace: (state) => (spaceName) => {
    const spaceData = state.spaces[spaceName]
    if (!spaceData) return []
    const seen = new Set()
    const users = []
    for (const [, data] of Object.entries(spaceData.members)) {
      if (!seen.has(data.user_id)) {
        seen.add(data.user_id)
        users.push({ user_id: data.user_id })
      }
    }
    return users.sort((a, b) => a.user_id - b.user_id)
  },
  getFocusEntriesByCell: (state) => (spaceName) => {
    const spaceData = state.spaces[spaceName]
    if (!spaceData) return EMPTY_FOCUS_MAP
    return _buildFocusMap(
      spaceData.members,
      'cell',
      (f) => `${f.row_id}:${f.field_id}`
    )
  },
  getFocusEntriesByRow: (state) => (spaceName) => {
    const spaceData = state.spaces[spaceName]
    if (!spaceData) return EMPTY_FOCUS_MAP
    return _buildFocusMap(spaceData.members, 'row', (f) => f.row_id)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
